@if (isOpen()) {
  <div
    class="fixed inset-0 bg-gray-900 bg-opacity-50 dark:bg-opacity-80 z-[60] flex items-center justify-center"
    (click)="onBackdropClick($event)"
  >
    <div class="relative p-4 w-full max-w-5xl max-h-full">
      <div
        #dialogPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="super-admin-user-dialog-title"
        class="relative bg-white rounded-lg shadow dark:bg-gray-700 flex flex-col max-h-[90vh]"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
          <h3 id="super-admin-user-dialog-title" class="text-xl font-semibold text-gray-900 dark:text-white">Super Admin User</h3>
          <button
            type="button"
            data-dialog-close
            (click)="onClose()"
            [disabled]="isSigningOut"
            class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
            </svg>
            <span class="sr-only">Close</span>
          </button>
        </div>

        <!-- Body: profile | platform notifications -->
        <div class="grid grid-cols-1 md:grid-cols-[18rem_1fr] divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-600 overflow-y-auto">
          <!-- Profile -->
          <div class="p-4 md:p-5 space-y-5">
            @if (user(); as u) {
              <div class="flex items-center gap-4">
                <div class="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-black text-xl font-semibold text-white">
                  {{ initials() }}
                </div>
                <div class="min-w-0">
                  <p class="truncate text-base font-semibold text-gray-900 dark:text-white">{{ u.displayName || 'Super admin' }}</p>
                  <p class="truncate text-sm text-gray-500 dark:text-gray-400">{{ u.email }}</p>
                </div>
              </div>
              <dl class="space-y-3 text-sm">
                <div>
                  <dt class="text-xs uppercase tracking-wide text-gray-400">Role</dt>
                  <dd class="text-gray-900 dark:text-white">Super admin</dd>
                </div>
                <div>
                  <dt class="text-xs uppercase tracking-wide text-gray-400">Environment</dt>
                  <dd>
                    <span
                      class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                      [class]="isProduction ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-100'"
                    >{{ environmentName }}</span>
                  </dd>
                </div>
                <div>
                  <dt class="text-xs uppercase tracking-wide text-gray-400">User ID</dt>
                  <dd class="break-all font-mono text-xs text-gray-700 dark:text-gray-300">{{ u.uid }}</dd>
                </div>
              </dl>
            } @else {
              <p class="text-sm text-gray-500 dark:text-gray-400">Not signed in.</p>
            }
            <button
              type="button"
              (click)="signOut()"
              [disabled]="isSigningOut"
              class="w-full text-white bg-black hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-black dark:hover:bg-gray-800 dark:focus:ring-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ isSigningOut ? 'Signing out…' : 'Sign out' }}
            </button>
          </div>

          <!-- Platform notifications -->
          <div class="p-4 md:p-5 space-y-6">
            <div>
              <h4 class="text-base font-semibold text-gray-900 dark:text-white">Platform notifications</h4>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Personal email alerts for platform-level events. Sent to
                <span class="font-medium text-gray-700 dark:text-gray-200">{{ deliveryEmail() || 'your account email' }}</span>.
              </p>
            </div>

            @if (toggleError(); as err) {
              <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{{ err }}</div>
            }

            <ul class="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-600 dark:border-gray-600">
              @for (event of events; track event.eventType) {
                <li class="flex items-start justify-between gap-4 p-4">
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-900 dark:text-white">{{ event.label }}</p>
                    <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ event.description }}</p>
                    <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">{{ event.detail }}</p>
                  </div>
                  <label class="relative inline-flex shrink-0 cursor-pointer items-center" [class.opacity-60]="rowsLoading() || isPending(event.eventType)">
                    <input
                      type="checkbox"
                      class="sr-only peer"
                      [checked]="isEnabled(event.eventType)"
                      [disabled]="rowsLoading() || isPending(event.eventType)"
                      (change)="toggle(event.eventType)"
                      [attr.aria-label]="event.label + ' alerts'"
                    />
                    <div class="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-black peer-focus:ring-2 peer-focus:ring-gray-300 dark:bg-gray-600 transition-colors"></div>
                    <div class="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-full flex items-center justify-center">
                      @if (isPending(event.eventType)) {
                        <svg class="h-3 w-3 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                      }
                    </div>
                  </label>
                </li>
              }
            </ul>

            <!-- Recent alerts -->
            <div>
              <h5 class="text-sm font-semibold text-gray-900 dark:text-white">Recent alerts</h5>
              @if (recentSent(); as sent) {
                @if (sent.length === 0) {
                  <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">No alerts have been sent to you yet.</p>
                } @else {
                  <ul class="mt-2 divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-600 dark:border-gray-600">
                    @for (s of sent; track s.id) {
                      <li class="flex items-start justify-between gap-3 px-4 py-3">
                        <div class="min-w-0">
                          <p class="truncate text-sm font-medium text-gray-900 dark:text-white">{{ s.title }}</p>
                          <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ s.body }}</p>
                          @if (s.status === 'failed' && s.error) {
                            <p class="mt-0.5 text-xs text-red-600">{{ s.error }}</p>
                          }
                        </div>
                        <div class="shrink-0 text-right">
                          <span
                            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                            [class]="s.status === 'failed' ? 'bg-red-100 text-red-800' : s.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-100'"
                          >{{ s.status === 'failed' ? 'Failed' : s.status === 'pending' ? 'Sending' : 'Sent' }}</span>
                          <p class="mt-1 text-xs text-gray-400">{{ relativeTime(s.createdAt) }}</p>
                        </div>
                      </li>
                    }
                  </ul>
                }
              } @else {
                <div class="mt-2 space-y-2">
                  <div class="h-10 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-600"></div>
                  <div class="h-10 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-600"></div>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end p-4 md:p-5 border-t border-gray-200 rounded-b dark:border-gray-600 gap-2">
          <button
            type="button"
            (click)="onClose()"
            [disabled]="isSigningOut"
            class="py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-900 hover:bg-gray-100 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-400 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
}
